import { useFormik } from "formik";
import _ from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useLocationStore, usePipedriveStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useCompanyValidationSchema from "../../hooks/useCompanyValidationSchema";
import useOrganizationUpdate from "../../hooks/useOrganizationUpdate";
import useRowClassName from "../../hooks/useRowClassName";
import DetailsType from "../../interfaces/DetailsType";
import ILocation from "../../interfaces/ILocation";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import editStyles from "../Edit.module.css";
import EditControls from "../EditControls";
import CompanyIcon from "../icons/CompanyIcon";
import StringInput from "../inputs/StringInput";
import LocationsEditableGrid from "../locations/LocationsEditableGrid";
import OnMapSpinner from "../spinners/OnMapSpinner";

function CompanyEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const organizationUpdate = useOrganizationUpdate();
    const locationStore = useLocationStore();
    const pipedriveStore = usePipedriveStore();
    const company = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id.toString() === id)), [id]).get();
    const locations = useMemo(() => computed(() => company ? _.orderBy(locationStore.locations.filter(l => l.pipedriveCompanyId === company.id), l => l.createdAt, "asc") : []), [company, locationStore.locations]).get();
    const validationSchema = useCompanyValidationSchema();
    const [locationsState, setLocationsState] = useState<ILocation[]>([]);
    const rowClassName = useRowClassName();

    const formik = useFormik<ICompanyState>({
        initialValues: CompanyState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            if (isCompanyStateChanged())
                await organizationUpdate({
                    ...company!,
                    name: values.name!,
                });

            const promises = locationsState.map(async ls => {
                const location = locations.find(l => l.id === ls.id)!;

                if (isLocationStateChanged(location, ls))
                    await locationStore.updateLocation({
                        ...location,
                        ...ls,
                    });
            });

            await Promise.all(promises);
            cancel();
        },
    })

    const close = () => navigate("/");
    const cancel = () => navigate(`/companies/${company!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const isLocationStateChanged = (location: ILocation, locationState: ILocation) => {
        return location.siteName !== locationState.siteName
            || location.address !== locationState.address
            || location.zoning !== locationState.zoning
            || location.buildingSize !== locationState.buildingSize
            || location.landSize !== locationState.landSize
            || location.ownerName !== locationState.ownerName
            || location.dateSold !== locationState.dateSold
            || location.salePrice !== locationState.salePrice
            || location.ownerAddress !== locationState.ownerAddress
            || location.notes !== locationState.notes;
    }

    const isCompanyStateChanged = () => {
        return company!.name !== formik.values.name;
    };

    const isLocationsStateChanged = () => {
        return locationsState.some(ls => isLocationStateChanged(locations.find(l => l.id === ls.id)!, ls));
    }

    useEffect(() => {
        if (company) {
            formik.resetForm({
                values: {
                    name: company.name,
                },
            });
        }
    }, [company]);

    useEffect(() => {
        setLocationsState(locations.map(l => ({ ...l })));
    }, [locations]);

    if (pipedriveStore.isOrganizationsLoading.fetching || locationStore.isLoading)
        return <OnMapSpinner />;

    if (!company)
        return null;

    return (
        <DetailsPanel type={DetailsType.CompanyEdit} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <CompanyIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>{company.name}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <div className={editStyles.container}>
                    <div className={editStyles.column}>
                        <div className={commonStyles.header}>Company Details</div>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="NAME" name="name" />
                        </div>
                    </div>
                    {!!locations.length && <LocationsEditableGrid values={locationsState} onChange={() => setLocationsState([...locationsState])} />}
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={isCompanyStateChanged() || isLocationsStateChanged()}
                        isUpdateLoading={pipedriveStore.isOrganizationsLoading.updating!}
                        onUpdate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}></div>
            </div>
        </DetailsPanel>
    );
}

export default observer(CompanyEdit);

interface ICompanyState {
    name: string | null;
}

class CompanyState implements ICompanyState {
    name: string | null = null;

    static empty() {
        return new CompanyState();
    }
}